import { AvailableTemplate, DataCell, ExcelExportDto, TableColumn } from '@ecosystem/export-api';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import {
  CreateImagePowerPointParams,
  CreateMultiImagePowerPointParams,
  CreatePowerPointTableParams,
  ExcelExportParams,
  ExcelRowArgs,
  ExportChartArgs,
  PERFORMANCE_BY_SEGMENTS,
} from '../Model';
import { saveFile } from '../SaveFile';
import { ExportContext, PageName } from '@utils/pageConfig/Types';
import { getLineChartExportColumn, getLineChartExportRow } from './ExportMultiChartExcel';
import { formatKPILabel, getContext, getFilename } from '../ExportUtils';
import { createImage, createMultiChartImage, createMultiImagePowerPoint, createPowerPoint } from '../ExportChart';
import { PerformanceAnalysisView } from '@components/performance-analysis-card/Types';
import { createChartAndTable } from '../export-table/ExportTableAndLineChart';
import { ProductsCompareKpi } from '@models/Products';
import { constructCurrencyLabel, constructCurrencySymbol } from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { AVERAGE_SELLING_PRICE } from '@pages/Utilities';
import {
  getTrendStackChartExportColumn,
  getTrendStackChartExportRow,
} from '@hooks/export/export-stacked-chart/export-trended-stacked-chart/TrendedStackedChartExcel';

const createExcel = async ({
  filename,
  exportFn,
  appliedCountry,
  appliedPg,
  data,
  stackTrendChartExcelData,
  factRevenue,
  excelRowArgs,
  title,
  pageName,
  volume = DEFAULT_VOLUME,
  loadedCountries,
  loadedProductGroups,
  pagesConfig,
}: ExcelExportParams) => {
  const currencyLabel = constructCurrencyLabel(factRevenue);
  const currencySymbol = constructCurrencySymbol(factRevenue);
  const dataColumns: TableColumn[] = stackTrendChartExcelData
    ? getTrendStackChartExportColumn(stackTrendChartExcelData.categories, excelRowArgs.showPerformanceWithin)
    : getLineChartExportColumn(data, excelRowArgs.showPerformanceWithin, pageName);
  const dataRows: DataCell[][] = stackTrendChartExcelData
    ? getTrendStackChartExportRow(stackTrendChartExcelData.series)
    : getLineChartExportRow(data, currencyLabel, currencySymbol, pageName, volume);

  const exportContext: ExportContext = {
    appliedCountry: appliedCountry,
    appliedPg: appliedPg,
    timePeriod: excelRowArgs.timePeriod,
    comparisonTimePeriod: excelRowArgs.comparisonTimePeriod,
    periodType: excelRowArgs.periodType,
    showPerformanceWithin: excelRowArgs.showPerformanceWithin,
    appliedFiltersText: excelRowArgs.appliedFiltersText,
    pageName: pageName,
    pagesConfig: pagesConfig,
    loadedCountries,
    loadedProductGroups,
  };

  const exportData: ExcelExportDto = {
    template: AvailableTemplate.Excel,
    name: filename,
    worksheets: [
      {
        worksheetName: title,
        context: getContext(exportContext),
        data: {
          dataRows,
          dataColumns,
          columnWidths: dataColumns.map((_, index: number) => ({
            columnNumber: index + 1,
            width: currencyLabel === '' ? 25 : 27, // different width can be configured via the column number
          })),
          currencySymbol: factRevenue.currencySymbol,
        },
      },
    ],
  };

  const exportFile = await exportFn(exportData);
  saveFile(exportFile);
};

export async function createExportFormats(args: ExportChartArgs): Promise<void> {
  const {
    exportFn,
    exportFormats,
    invokeExport,
    title,
    exportImage,
    appliedCountry,
    appliedPg,
    data,
    stackTrendChartExcelData,
    timePeriod,
    comparisonTimePeriod,
    factRevenue,
    periodType,
    showPerformanceWithin,
    appliedFiltersText,
    selectedKpi,
    sortedBy,
    chartTitles,
    pageName,
    chartSubTitles,
    multipleChartsExport,
    volume = DEFAULT_VOLUME,
    pagesConfig,
    loadedCountries,
    loadedProductGroups,
  } = args;

  async function exportToExcel() {
    if (
      exportFormats.includes(ExportFormat.Xlsx) &&
      [PageName.PERFORMANCE_ANALYSIS, PageName.RI_PRODUCT_TRENDS, PageName.EXPLORE].includes(pageName)
    ) {
      const rowArgs: ExcelRowArgs = {
        timePeriod,
        comparisonTimePeriod,
        periodType,
        showPerformanceWithin,
        appliedFiltersText,
        selectedKpi,
      };

      const filename = getFilename({
        title,
        appliedCountry,
        appliedPg,
        timeframe: timePeriod,
        comparisonTimePeriod,
        exportFormat: ExportFormat.Xlsx,
        selectedKpi,
        viewType: PerformanceAnalysisView.LineChart,
        pageName,
      });

      await createExcel({
        filename,
        exportFn,
        appliedCountry,
        appliedPg,
        data,
        stackTrendChartExcelData,
        factRevenue,
        excelRowArgs: rowArgs,
        title,
        pageName,
        volume,
        loadedCountries,
        loadedProductGroups,
        pagesConfig,
      });
    }
  }

  async function exportToPowerPoint() {
    if (exportFormats.includes(ExportFormat.Pptx)) {
      if (multipleChartsExport || (Array.isArray(exportImage) && exportImage.length > 0)) {
        //no type for kpi label for multi image exporting
        const newKPILabelPptx = formatKPILabel(pageName, selectedKpi);

        const filename = getFilename({
          title,
          appliedCountry,
          appliedPg,
          timeframe: timePeriod,
          comparisonTimePeriod,
          exportFormat: ExportFormat.Pptx,
          selectedKpi: newKPILabelPptx,
          viewType: PerformanceAnalysisView.LineChart,
          pageName,
        });
        if (PageName.RI_PRODUCT_COMPARE === pageName) {
          const methodParam: CreatePowerPointTableParams = {
            featureData: data as ProductsCompareKpi,
            imageData: [],
            exportImage,
            filename,
            exportFn,
            appliedCountry,
            appliedPg,
            timePeriod,
            comparisonTimePeriod,
            periodType,
            appliedFiltersText,
            selectedKpi,
            chartTitles: chartTitles!,
            chartSubTitles: chartSubTitles!,
            multipleChartsExport,
            currency: factRevenue.currencySymbol,
            pageName,
          };
          await createChartAndTable(methodParam);
        } else {
          const methodParam: CreateMultiImagePowerPointParams = {
            filename,
            exportFn,
            exportImage,
            appliedCountry,
            appliedPg,
            timePeriod,
            comparisonTimePeriod,
            periodType,
            showPerformanceWithin,
            appliedFiltersText,
            selectedKpi,
            sortedBy,
            segmentBy: '',
            chartTitles: chartTitles!,
            chartSubTitles: chartSubTitles!,
            multipleChartsExport,
            pageName,
            loadedCountries,
            loadedProductGroups,
            pagesConfig,
          };
          await createMultiImagePowerPoint(methodParam);
        }
      } else {
        const filename = getFilename({
          title,
          appliedCountry,
          appliedPg,
          timeframe: timePeriod,
          comparisonTimePeriod,
          exportFormat: ExportFormat.Pptx,
          selectedKpi,
          viewType: PerformanceAnalysisView.LineChart,
          pageName: pageName,
        });

        const subTitle = title?.includes(AVERAGE_SELLING_PRICE) ? '' : selectedKpi.label;
        const methodParam: CreateImagePowerPointParams = {
          filename,
          exportFn,
          appliedCountry,
          appliedPg,
          timePeriod,
          comparisonTimePeriod,
          periodType,
          showPerformanceWithin,
          appliedFiltersText,
          invokeExport,
          selectedKpi,
          segmentBy: '',
          title: title,
          exportImage,
          subTitle: subTitle,
          pageName: pageName,
          pagesConfig: pagesConfig,
          loadedCountries,
          loadedProductGroups,
        };

        await createPowerPoint(methodParam);
      }
    }
  }

  async function exportToPNG() {
    if (exportFormats.includes(ExportFormat.Png)) {
      if (multipleChartsExport || (Array.isArray(exportImage) && exportImage.length)) {
        //no type for kpi label for multi image exporting
        const newKPILabelPng = formatKPILabel(pageName, selectedKpi);

        const filename = getFilename({
          title: title,
          appliedCountry: appliedCountry,
          appliedPg: appliedPg,
          timeframe: timePeriod,
          comparisonTimePeriod: comparisonTimePeriod,
          exportFormat: ExportFormat.Pptx,
          selectedKpi: newKPILabelPng,
          viewType: PerformanceAnalysisView.LineChart,
          pageName: pageName,
        });

        const methodParams: CreateMultiImagePowerPointParams = {
          filename: filename,
          exportFn: exportFn,
          exportImage,
          appliedCountry: appliedCountry,
          appliedPg: appliedPg,
          timePeriod: timePeriod,
          comparisonTimePeriod: comparisonTimePeriod,
          periodType: periodType,
          showPerformanceWithin: showPerformanceWithin,
          appliedFiltersText: appliedFiltersText,
          selectedKpi: selectedKpi,
          sortedBy: sortedBy,
          segmentBy: '',
          chartTitles: chartTitles!,
          chartSubTitles: chartSubTitles!,
          multipleChartsExport: multipleChartsExport,
          pageName: pageName,
          loadedCountries,
          loadedProductGroups,
          pagesConfig: pagesConfig,
        };

        await createMultiChartImage(methodParams);
      } else {
        const filename = getFilename({
          title,
          appliedCountry,
          appliedPg,
          timeframe: timePeriod,
          comparisonTimePeriod,
          exportFormat: ExportFormat.Png,
          selectedKpi,
          viewType: PerformanceAnalysisView.LineChart,
          pageName: pageName,
        });

        const methodParam: CreateImagePowerPointParams = {
          filename: filename,
          exportFn,
          exportImage,
          appliedCountry,
          appliedPg,
          timePeriod,
          comparisonTimePeriod,
          periodType,
          showPerformanceWithin,
          appliedFiltersText,
          invokeExport,
          selectedKpi,
          segmentBy: '',
          title: '',
          subTitle: '',
          pageName: pageName,
          pagesConfig: pagesConfig,
          loadedCountries,
          loadedProductGroups,
        };

        let chartTitle = title?.includes(AVERAGE_SELLING_PRICE)
          ? AVERAGE_SELLING_PRICE
          : `${PERFORMANCE_BY_SEGMENTS} \n${methodParam.selectedKpi.label}`;

        await createImage(methodParam, chartTitle);
      }
    }
  }

  await exportToExcel();
  await exportToPowerPoint();
  await exportToPNG();
}
